import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { Activity, ShieldAlert, AlertCircle } from 'lucide-react';

const PORTAL_URL = 'https://portal.amslerlabs.com';

const ProtectedRoute = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { hasPulseLabAccess, loading: orgLoading, userRole, error: orgError, currentOrganization } = useOrganization();

  if (authLoading || orgLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#003F5C] via-[#3D4752] to-[#5E4B8B] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm mb-4">
            <Activity className="w-8 h-8 text-[#2C9C7A] animate-spin" />
          </div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated — send to portal login, not a local login page
  if (!user) {
    window.location.href = `${PORTAL_URL}/login?redirect=/api/apps/pulselab/launch`;
    return null;
  }

  // Org failed to load
  if (orgError || !currentOrganization) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#003F5C] via-[#3D4752] to-[#5E4B8B] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-500/20 mb-4">
            <AlertCircle className="w-8 h-8 text-yellow-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Organization Not Found</h1>
          <p className="text-gray-300 mb-6">
            Your account is not linked to an organization. Please contact your administrator.
          </p>
          <a
            href={PORTAL_URL}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#2C9C7A] hover:bg-[#168980] text-white rounded-lg transition-colors"
          >
            Return to Portal
          </a>
        </div>
      </div>
    );
  }

  // Wrong role — send back to portal
  if (!hasPulseLabAccess()) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#003F5C] via-[#3D4752] to-[#5E4B8B] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#B0413E]/20 mb-4">
            <ShieldAlert className="w-8 h-8 text-[#B0413E]" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-gray-300 mb-4">
            PulseLab is only accessible to Organization Administrators.
          </p>
          <p className="text-sm text-gray-400 mb-6">
            Your current role:{' '}
            <span className="font-semibold text-white">{userRole || 'Unknown'}</span>
          </p>
          <a
            href={PORTAL_URL}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
          >
            Return to Portal
          </a>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;